import React, { useState } from 'react';
import { useDuplicateDetection } from '../hooks/useDuplicateDetection';
import { useLanguage } from '../contexts/LanguageContext';
import { getDupLang } from '../translations/dup_lang';
import { DuplicateMatch, DuplicateField } from '../lib/services/duplicateService';
import { Customer } from '../types/customer';
import CommonPageHeader from '../components/common/CommonPageHeader';
import DuplicateMergeModal from '../components/customers/DuplicateMergeModal';
import {
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const fieldLabels: Record<DuplicateField, (tr: any) => string> = {
  email: (tr) => tr.dup.fieldEmail,
  phone: (tr) => tr.dup.fieldPhone,
  taxNumber: (tr) => tr.dup.fieldTaxNumber,
  companyName: (tr) => tr.dup.fieldCompanyName,
  fullName: (tr) => tr.dup.fieldFullName,
};

const confidenceColors: Record<string, string> = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

const DuplicateScanner: React.FC = () => {
  const { language } = useLanguage();
  const tr = getDupLang(language);
  const { scanning, scanResult, scanAll, merge } = useDuplicateDetection();
  const [mergeTarget, setMergeTarget] = useState<DuplicateMatch | null>(null);

  const getCustomerName = (c: Customer) => {
    if (c.companyName) return c.companyName;
    if (c.firstName || c.lastName) return `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return c.email || c.id;
  };

  return (
    <div className="space-y-4 w-full min-w-0">
      <CommonPageHeader
        title={tr.dup.title}
        subtitle={tr.dup.subtitle}
        icon={MagnifyingGlassIcon}
      >
        <button
          onClick={scanAll}
          disabled={scanning}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors disabled:opacity-50"
        >
          {scanning ? (
            <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
          )}
          <span>{scanning ? tr.dup.scanning : tr.dup.scan}</span>
        </button>
      </CommonPageHeader>

      {/* Summary Cards */}
      {scanResult && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <CheckCircleIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{scanResult.totalCustomers}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{tr.dup.totalCustomers}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{scanResult.duplicates.length}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{tr.dup.duplicatesFound.replace('{count}', String(scanResult.duplicates.length))}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{scanResult.totalDuplicates}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{tr.dup.totalDuplicates}</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Results */}
      {scanResult && scanResult.duplicates.length === 0 && (
        <div className="text-center py-16">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="w-20 h-20 mx-auto bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg"
          >
            <CheckCircleIcon className="w-10 h-10 text-white" />
          </motion.div>
          <p className="text-gray-500 dark:text-gray-400 text-lg">{tr.dup.noDuplicates}</p>
        </div>
      )}

      {/* Duplicate Groups */}
      <AnimatePresence>
        {scanResult && scanResult.duplicates.length > 0 && (
          <div className="space-y-3">
            {scanResult.duplicates.map((dup, index) => (
              <motion.div
                key={dup.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                {/* Group Header */}
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${confidenceColors[dup.confidence]}`}>
                      {dup.confidence === 'high' ? tr.dup.confidenceHigh : dup.confidence === 'medium' ? tr.dup.confidenceMedium : tr.dup.confidenceLow}
                    </span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {fieldLabels[dup.field](tr)}: <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{dup.value}</code>
                    </span>
                    <span className="text-xs text-gray-400">({dup.customers.length} ügyfél)</span>
                  </div>
                  <button
                    onClick={() => setMergeTarget(dup)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all"
                  >
                    {tr.dup.merge}
                  </button>
                </div>

                {/* Customer Cards */}
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {dup.customers.map((c) => (
                    <div key={c.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-900/50">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white text-xs font-bold">
                          {getCustomerName(c).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{getCustomerName(c)}</p>
                          <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                            {c.email && <span>{c.email}</span>}
                            {c.phone && <span>• {c.phone}</span>}
                            {c.taxNumber && <span>• {c.taxNumber}</span>}
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {c.createdAt ? new Date(c.createdAt as any).toLocaleDateString() : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Merge Modal */}
      {mergeTarget && (
        <DuplicateMergeModal
          match={mergeTarget}
          onClose={() => setMergeTarget(null)}
          onMerge={merge}
        />
      )}
    </div>
  );
};

export default DuplicateScanner;
